import { GUIDED_OPERATIONS_FIXTURE } from "../data/guidedOperations.ts";
import { responseOptions, scenarioEvidence } from "../data/seed.ts";
import type {
  AuditReceipt,
  HumanResponseDecision,
  OverridableReadinessRuleId,
  ReadinessOverride,
  RecoveryVerification,
  ResponseOption,
  ScenarioInteractionEvent,
  ScenarioStage,
} from "../domain";

export type ScenarioState = {
  stage: ScenarioStage;
  selectedTraceNodeId: string;
  linkedEvidenceIds: string[];
  decisionId: ResponseOption["id"] | null;
  acknowledgedBy: string | null;
  assignedOwner: {
    id: string;
    name: string;
  } | null;
  readinessOverrides: ReadinessOverride[];
  humanDecision: HumanResponseDecision | null;
  recoveryVerification: RecoveryVerification | null;
  receipts: AuditReceipt[];
  drawerOpen: boolean;
  guideOpen: boolean;
  guideStep: number;
};

export type ScenarioAction =
  | { type: "start"; actor: string }
  | { type: "reset" }
  | { type: "select-node"; nodeId: string }
  | { type: "close-drawer" }
  | { type: "link-evidence"; evidenceId: string; actor: string }
  | { type: "revise-response"; actor: string }
  | { type: "record-interaction"; event: ScenarioInteractionEvent }
  | {
      type: "record-readiness-override";
      canApproveSensitiveChanges: boolean;
      event: Extract<ScenarioInteractionEvent, { type: "readiness-override-recorded" }>;
    }
  | {
      type: "decide-response";
      canApproveSensitiveChanges: boolean;
      event: Extract<ScenarioInteractionEvent, { type: "response-decided" }>;
    }
  | { type: "record-artifact"; artifact: "teams" | "report" | "handoff"; actor: string }
  | { type: "open-guide" }
  | { type: "close-guide" }
  | { type: "guide-next" }
  | { type: "guide-back" };

export const initialScenarioState: ScenarioState = {
  stage: "calm",
  selectedTraceNodeId: "INC-2048",
  linkedEvidenceIds: [],
  decisionId: null,
  acknowledgedBy: null,
  assignedOwner: null,
  readinessOverrides: [],
  humanDecision: null,
  recoveryVerification: null,
  receipts: [],
  drawerOpen: false,
  guideOpen: false,
  guideStep: 0,
};

function receiptTime(receiptCount: number) {
  const totalMinutes = 10 * 60 + 31 + receiptCount * 2;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

function makeReceipt(
  state: ScenarioState,
  input: Omit<AuditReceipt, "id" | "occurredAt">,
): AuditReceipt {
  return {
    ...input,
    id: `RCP-2048-${String(state.receipts.length + 1).padStart(2, "0")}`,
    occurredAt: receiptTime(state.receipts.length),
  };
}

function applyInteractionEvent(
  state: ScenarioState,
  event: ScenarioInteractionEvent,
): ScenarioState {
  if (state.stage === "calm" || !event.reason.trim()) return state;

  switch (event.type) {
    case "incident-acknowledged": {
      if (state.acknowledgedBy) return state;
      const receipt = makeReceipt(state, {
        actor: event.actor,
        action: "Acknowledged incident",
        recordId: event.incidentId,
        reason: event.reason,
        outcome: `${event.actor} accepted responsibility for initial triage`,
        kind: "acknowledgement",
      });
      return {
        ...state,
        acknowledgedBy: event.actor,
        receipts: [receipt, ...state.receipts],
      };
    }
    case "owner-assigned": {
      if (!event.ownerId.trim() || !event.ownerName.trim()) return state;
      const sameOwner =
        state.assignedOwner?.id === event.ownerId
        && state.assignedOwner.name === event.ownerName
      ;
      if (sameOwner && state.acknowledgedBy) return state;

      let nextState = state;
      if (!state.acknowledgedBy) {
        const acknowledgementReceipt = makeReceipt(nextState, {
          actor: event.actor,
          action: "Acknowledged incident",
          recordId: event.incidentId,
          reason: event.reason,
          outcome: `${event.actor} accepted responsibility for initial triage`,
          kind: "acknowledgement",
        });
        nextState = {
          ...nextState,
          acknowledgedBy: event.actor,
          receipts: [acknowledgementReceipt, ...nextState.receipts],
        };
      }

      if (sameOwner) return nextState;

      const receipt = makeReceipt(nextState, {
        actor: event.actor,
        action: "Assigned incident owner",
        recordId: event.incidentId,
        reason: event.reason,
        outcome: `${event.ownerName} is the accountable incident owner`,
        kind: "assignment",
      });
      return {
        ...nextState,
        assignedOwner: {
          id: event.ownerId,
          name: event.ownerName,
        },
        receipts: [receipt, ...nextState.receipts],
      };
    }
    case "readiness-override-recorded": {
      const override: ReadinessOverride = {
        ruleId: event.ruleId,
        actor: event.actor,
        reason: event.reason,
      };
      const duplicate = state.readinessOverrides.some(
        (item) => item.ruleId === override.ruleId,
      );
      if (duplicate) return state;
      const receipt = makeReceipt(state, {
        actor: event.actor,
        action: "Recorded readiness warning override",
        recordId: event.incidentId,
        reason: event.reason,
        outcome: `${event.ruleId} warning accepted for human review`,
        kind: "override",
      });
      return {
        ...state,
        readinessOverrides: [...state.readinessOverrides, override],
        receipts: [receipt, ...state.receipts],
      };
    }
    case "response-decided": {
      if (
        state.stage !== "identified"
        || !event.approverId.trim()
        || !event.approverName.trim()
      ) {
        return state;
      }
      const response = responseOptions.find((option) => option.id === event.responseId);
      if (!response) return state;
      const approved = event.decision === "approved";
      const requiredWarningOverrides = [
        "redundancy-known",
        "maintenance-window",
      ] as const;
      const warningOverridesRecorded = requiredWarningOverrides.every((ruleId) =>
        state.readinessOverrides.some((override) => override.ruleId === ruleId)
      );
      if (
        approved
        && (!state.acknowledgedBy
          || !state.assignedOwner
          || state.linkedEvidenceIds.length < GUIDED_OPERATIONS_FIXTURE.readiness.requiredEvidenceCount
          || !warningOverridesRecorded)
      ) {
        return state;
      }
      const humanDecision: HumanResponseDecision = {
        decision: event.decision,
        responseId: event.responseId,
        approverId: event.approverId,
        approverName: event.approverName,
      };
      const receipt = makeReceipt(state, {
        actor: event.actor,
        action: `${event.decision === "approved" ? "Approved" : "Rejected"} response: ${response.label}`,
        recordId: event.incidentId,
        reason: event.reason,
        outcome: approved
          ? response.result
          : "Response was not executed; the incident remains identified for revision",
        kind: "decision",
      });
      return {
        ...state,
        decisionId: approved ? response.id : null,
        humanDecision,
        recoveryVerification: null,
        stage: approved ? "monitoring" : "identified",
        receipts: [receipt, ...state.receipts],
      };
    }
    case "recovery-verified": {
      if (
        state.stage !== "monitoring"
        || state.humanDecision?.decision !== "approved"
      ) {
        return state;
      }
      const verification: RecoveryVerification = {
        serviceRecovered: event.serviceRecovered,
        endpointId: event.endpointId,
        endpointReregistered: event.endpointReregistered,
        verifiedBy: event.actor,
      };
      const verified = event.serviceRecovered && event.endpointReregistered;
      const receipt = makeReceipt(state, {
        actor: event.actor,
        action: "Verified service and endpoint recovery",
        recordId: event.incidentId,
        reason: event.reason,
        outcome: verified
          ? `${event.endpointId} re-registered and service recovery was verified`
          : "Recovery remains under monitoring because one or more checks did not pass",
        kind: "verification",
      });
      return {
        ...state,
        recoveryVerification: verification,
        stage: verified ? "recovered" : "monitoring",
        receipts: [receipt, ...state.receipts],
      };
    }
  }
}

function isProtectedInteraction(event: ScenarioInteractionEvent) {
  return event.type === "readiness-override-recorded"
    || event.type === "response-decided";
}

function hasWarningRule(
  event: Extract<ScenarioInteractionEvent, { type: "readiness-override-recorded" }>,
): event is Extract<ScenarioInteractionEvent, { type: "readiness-override-recorded" }> & {
  ruleId: OverridableReadinessRuleId;
} {
  return event.ruleId === "redundancy-known" || event.ruleId === "maintenance-window";
}

export function scenarioReducer(state: ScenarioState, action: ScenarioAction): ScenarioState {
  switch (action.type) {
    case "start": {
      if (state.stage !== "calm") return state;
      const receipt = makeReceipt(state, {
        actor: action.actor,
        action: "Started controlled outage simulation",
        recordId: "INC-2048",
        reason: "Demonstrate the North Academic response workflow",
        outcome: "Correlated service and infrastructure alerts activated",
        kind: "scenario",
      });
      return {
        ...state,
        stage: "investigating",
        selectedTraceNodeId: "INC-2048",
        receipts: [receipt],
      };
    }
    case "reset":
      return initialScenarioState;
    case "select-node":
      return { ...state, selectedTraceNodeId: action.nodeId, drawerOpen: true };
    case "close-drawer":
      return { ...state, drawerOpen: false };
    case "link-evidence": {
      if (state.linkedEvidenceIds.includes(action.evidenceId)) return state;
      const evidence = scenarioEvidence.find((item) => item.id === action.evidenceId);
      if (!evidence) return state;
      const linkedEvidenceIds = [...state.linkedEvidenceIds, action.evidenceId];
      const receipt = makeReceipt(state, {
        actor: action.actor,
        action: "Linked evidence to incident",
        recordId: evidence.id,
        reason: evidence.supports,
        outcome: `${evidence.label} is now part of the decision record`,
        kind: "evidence",
      });
      return {
        ...state,
        stage: linkedEvidenceIds.length >= 2 ? "identified" : state.stage,
        linkedEvidenceIds,
        receipts: [receipt, ...state.receipts],
      };
    }
    case "revise-response": {
      const receipt = makeReceipt(state, {
        actor: action.actor,
        action: "Reopened response decision",
        recordId: "INC-2048",
        reason: "Review the consequence of the current branch",
        outcome: "Incident returned to identified state without losing evidence",
        kind: "decision",
      });
      return {
        ...state,
        decisionId: null,
        humanDecision: null,
        recoveryVerification: null,
        stage: "identified",
        receipts: [receipt, ...state.receipts],
      };
    }
    case "record-interaction": {
      if (isProtectedInteraction(action.event)) return state;
      return applyInteractionEvent(state, action.event);
    }
    case "record-readiness-override": {
      if (
        !action.canApproveSensitiveChanges
        || state.stage !== "identified"
        || !hasWarningRule(action.event)
      ) {
        return state;
      }
      return applyInteractionEvent(state, action.event);
    }
    case "decide-response": {
      if (!action.canApproveSensitiveChanges) return state;
      return applyInteractionEvent(state, action.event);
    }
    case "record-artifact": {
      const labels = {
        teams: ["Previewed Teams restoration update", "Preview only; no message was delivered"],
        report: ["Generated incident report", "Current scenario state rendered into an export-ready report"],
        handoff: ["Generated shift handoff", "Open work, evidence, and recovery state summarized"],
      } as const;
      const [actionLabel, outcome] = labels[action.artifact];
      const receipt = makeReceipt(state, {
        actor: action.actor,
        action: actionLabel,
        recordId: "INC-2048",
        reason: "Prepare an accountable communication artifact",
        outcome,
        kind: action.artifact === "teams" ? "communication" : "report",
      });
      return { ...state, receipts: [receipt, ...state.receipts] };
    }
    case "open-guide":
      return { ...state, guideOpen: true };
    case "close-guide":
      return { ...state, guideOpen: false };
    case "guide-next":
      return { ...state, guideStep: Math.min(state.guideStep + 1, 5) };
    case "guide-back":
      return { ...state, guideStep: Math.max(state.guideStep - 1, 0) };
  }
}
